// src/oura/merchant/brandWait.ts
//
// An Online client logs in before any AI check (JOURNEY-SPEC v1.1 §2.4a, O9):
// its first check waits for brand checks to go live. Home and Visibility then
// say so in one line instead of "not measured yet".
//
// The client's own onboarding record says it, as `waitingFor: "brand"` on
// GET /businesses/mine/onboarding (the staff record's `scan.waitingFor`). Read
// defensively: a backend or sample store that does not send it yet reads as
// not waiting, which keeps today's words.

import { useEffect, useState } from "react";
import { getMyOnboarding, type MerchantOnboarding } from "@service/aeo/onboarding";

/** The one line both screens show while the first check waits for brand checks. (placeholder until H8 ships) */
export const BRAND_WAIT_LINE = "Your first AI check runs when brand checks go live.";

/** True when the client's record says its first check waits for brand checks. */
export function waitsForBrand(m: MerchantOnboarding | null): boolean {
    if (!m) return false;
    const wire = m as MerchantOnboarding & { waitingFor?: string | null; scan?: { waitingFor?: string | null } | null };
    return wire.waitingFor === "brand" || wire.scan?.waitingFor === "brand";
}

/** Whether this client's first check waits for brand checks. False while asking, and off for staff. */
export function useWaitsForBrand(enabled: boolean): boolean {
    const [waits, setWaits] = useState(false);
    useEffect(() => {
        setWaits(false);
        if (!enabled) return;
        let cancelled = false;
        getMyOnboarding().then(
            (m) => { if (!cancelled) setWaits(waitsForBrand(m)); },
            () => { if (!cancelled) setWaits(false); },
        );
        return () => { cancelled = true; };
    }, [enabled]);
    return waits;
}
